import axiosInstance from '../services/api/axiosInstance';

/**
 * Preloads important dropdown data when a user logs in
 * This improves the user experience by fetching frequently used data ahead of time
 * @param {boolean} forceRefresh - Whether to force refresh all data even if cached
 * @param {string} teamId - Optional team ID to load data for (defaults to selectedTeam from localStorage)
 */
const preloadData = async (forceRefresh = false, teamId = null) => {
  try {
    const selectedTeam = teamId || localStorage.getItem("selectedTeam");
    if (!selectedTeam) {
      console.log("No team selected, skipping preload");
      return;
    }

    console.log("Preloading website and contract data for team:", selectedTeam);
    
    // Check if we've recently refreshed (within 10 seconds) to avoid rate limiting
    const now = Date.now();
    const lastPreloadTime = parseInt(sessionStorage.getItem("lastPreloadTime") || "0");
    
    if (!forceRefresh && now - lastPreloadTime < 10000) {
      console.log("Preload throttled - too recent. Using existing data.");
      return;
    }
    
    // If forceRefresh is true, clear existing cached data
    if (forceRefresh) {
      console.log("Force refresh requested, clearing existing data");
      sessionStorage.removeItem("preloadedWebsites");
      sessionStorage.removeItem("preloadedContracts");
    }
    
    // Fetch websites and smart contracts with timeout handling
    const results = await Promise.allSettled([
      preloadWebsites(selectedTeam),
      preloadSmartContracts(selectedTeam)
    ]);
    
    // Log results of each preload operation
    results.forEach((result, index) => {
      const operation = index === 0 ? 'websites' : 'contracts';
      if (result.status === 'rejected') {
        console.error(`Failed to preload ${operation}:`, result.reason);
      } else {
        console.log(`Successfully preloaded ${operation}`);
      }
    });
    
    // Update last preload time even if some operations failed
    sessionStorage.setItem("lastPreloadTime", now.toString());
    console.log("Preloading completed");
  } catch (error) {
    console.error("Error preloading data:", error);
  }
};

/**
 * Preloads website data for the selected team
 */
const preloadWebsites = async (teamId) => {
  try {
    console.log("Preloading websites for team:", teamId);
    
    // Check if we have cached websites and aren't forcing a refresh
    const cachedWebsites = sessionStorage.getItem("preloadedWebsites");
    const lastWebsitesRefreshTime = parseInt(sessionStorage.getItem("lastWebsitesRefreshTime") || "0");
    const now = Date.now();
    
    // If we have cached data and it's less than 5 minutes old, use it
    if (cachedWebsites && now - lastWebsitesRefreshTime < 300000) {
      console.log("Using cached website data (< 5 minutes old)");
      return JSON.parse(cachedWebsites);
    }
    
    try {
    const isObjectId = typeof teamId === 'string' && /^[a-fA-F0-9]{24}$/.test(teamId);
    const response = isObjectId
      ? await axiosInstance.get(`/website/team/${teamId}`)
      : await axiosInstance.get(`/website/name/${teamId}`);
      
      if (response.status === 200 && response.data.websites) {
        console.log(`Successfully preloaded ${response.data.websites.length} websites`);
        
        // Store in sessionStorage for quick access
        sessionStorage.setItem("preloadedWebsites", JSON.stringify(response.data.websites));
        sessionStorage.setItem("lastWebsitesRefreshTime", now.toString());
        
        // If no website is selected already, select the first one
        if (!localStorage.getItem("selectedWebsite") && response.data.websites.length > 0) {
          const firstWebsite = response.data.websites[0];
          localStorage.setItem("idy", firstWebsite.site_id || firstWebsite.siteId);
          localStorage.setItem("selectedWebsite", firstWebsite.domain || firstWebsite.Domain);
        }
        
        return response.data.websites;
      }
    } catch (apiError) {
      console.error("Error preloading websites:", apiError);
      
      // If we have cached data (even if older), use it as fallback
      if (cachedWebsites) {
        console.log("API failed, falling back to cached website data");
        return JSON.parse(cachedWebsites);
      }
      
      // If it's a timeout error, don't throw - just return empty array
      if (apiError.code === 'ECONNABORTED' && apiError.message.includes('timeout')) {
        console.warn("Website preload timed out - continuing without website data");
        return [];
      }
      
      throw apiError; // Re-throw non-timeout errors
    }
  } catch (error) {
    console.error("Error preloading websites:", error);
    return [];
  }
};

/**
 * Preloads smart contract data for the selected team
 */
const preloadSmartContracts = async (teamId) => {
  try {
    console.log("Preloading smart contracts for team:", teamId);
    
    // Check if we have cached contracts and aren't forcing a refresh
    const cachedContracts = sessionStorage.getItem("preloadedContracts");
    const lastContractsRefreshTime = parseInt(sessionStorage.getItem("lastContractsRefreshTime") || "0");
    const now = Date.now();
    
    // If we have cached data and it's less than 5 minutes old, use it
    if (cachedContracts && now - lastContractsRefreshTime < 300000) {
      console.log("Using cached contracts data (< 5 minutes old)");
      return JSON.parse(cachedContracts);
    }
    
    try {
      const response = await axiosInstance.get(`/smart-contracts/team/${teamId}`);
      
      if (response.data && response.data.contracts) {
        // Format contract data
        const contracts = response.data.contracts.map(contract => {
          const contractType = contract.contractType || 'main';
          console.log(`Preload loading contract: ${contract.name} with type: ${contractType} (raw: ${contract.contractType})`);
          
          return {
            id: contract.contractId,
            address: contract.address,
            name: contract.name || `Contract ${contract.address.substring(0, 6)}...${contract.address.substring(contract.address.length - 4)}`,
            blockchain: contract.blockchain,
            tokenSymbol: contract.tokenSymbol,
            contractType: contractType,
            stakingDetails: contract.stakingDetails || null
          };
        });
        
        // Store in sessionStorage for quick access
        sessionStorage.setItem("preloadedContracts", JSON.stringify(contracts));
        sessionStorage.setItem("lastContractsRefreshTime", now.toString());
        
        console.log(`Successfully preloaded ${contracts.length} contracts`);
        return contracts;
      }
    } catch (apiError) {
      console.error("Error preloading contracts:", apiError);
      
      // If we have cached data (even if older), use it as fallback
      if (cachedContracts) {
        console.log("API failed, falling back to cached contract data");
        return JSON.parse(cachedContracts);
      }
      
      // If it's a timeout error, don't throw - just return empty array
      if (apiError.code === 'ECONNABORTED' && apiError.message.includes('timeout')) {
        console.warn("Contract preload timed out - continuing without contract data");
        return [];
      }
      
      throw apiError; // Re-throw non-timeout errors
    }
  } catch (error) {
    console.error("Error preloading contracts:", error);
    return [];
  }
};

export default preloadData; 